/**
 * Path: packages/frontend/src/store/project.ts
 * Task: Global Pinia Store managing active project context, metadata, and analytics.
 * Update: Added reactive synchronization of project-specific capabilities to authStore upon project load.
 */
import { defineStore } from 'pinia';
import { useAuthStore } from './auth';
import { useUiStore } from './ui';
import { useDatastoreStore } from './datastore';
import { useWorkspaceStore } from './workspace';
import { useFilterStore } from './filters';
import { api, type ProjectMetadata } from '@/core/services/api';

export interface LoadedProject {
  value: string;
  title?: string;
  role?: string;
  [key: string]: any;
}

const LOCAL_STORAGE_PROJECT_KEY = 'unravel_active_project';

export const useProjectStore = defineStore('project', {
  state: () => {
    let hydratedProject = null;
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_PROJECT_KEY);
      if (stored) {
        hydratedProject = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to parse project from local storage', e);
    }
    return {
      loadedProject: hydratedProject as LoadedProject | null,
      projectsMetadata: {} as Record<string, ProjectMetadata>,
      projectStats: null as any | null,
      isNewProjectDialogOpen: false,
      isOpenProjectDialogOpen: false,
      isLoading: false,
      error: null as string | null
    };
  },

  getters: {
    projectOptions: (state) => {
      return Object.entries(state.projectsMetadata).map(([id, meta]: [string, any]) => ({
        value: id,
        title: meta.displayName || id,
        color: meta.color,
        icon: meta.icon,
        role: meta.role,
        visibility: meta.visibility
      }));
    }
  },

  actions: {
    setLoadedProject(project: LoadedProject | null) {
      const previousId = this.loadedProject?.value;
      this.loadedProject = project;

      if (!project) {
        this.projectStats = null;
        localStorage.removeItem(LOCAL_STORAGE_PROJECT_KEY);
      } else {
        localStorage.setItem(LOCAL_STORAGE_PROJECT_KEY, JSON.stringify(project));
      }

      const authStore = useAuthStore();
      authStore.refreshProjectCapabilities(project?.value);

      if (project?.value !== previousId) {
        try {
          const datastore = useDatastoreStore();
          const workspace = useWorkspaceStore();
          const filterStore = useFilterStore();

          datastore.activeProjectId = null;
          datastore.data = null;
          datastore.periods = null;

          workspace.setSelectedEntity(null);
          workspace.setSecondarySelectedEntity(null);

          filterStore.filterQuery = [{ id: 'root', types: [], instances: [] }];
          filterStore.statusFilter = [];
          filterStore.textFilter = '';
          filterStore.dynamicFilters = {};
        } catch (e) {
          console.warn('State purge during project switch skipped due to store initialization order.');
        }
      }
    },

    showNewProjectDialog() {
      this.isNewProjectDialogOpen = true;
    },

    showOpenProjectDialog() {
      if (Object.keys(this.projectsMetadata).length === 0) {
        this.fetchProjectsMetadata();
      }
      this.isOpenProjectDialogOpen = true;
    },

    trackRecentProject(projectId: string, title: string) {
      const authStore = useAuthStore();
      if (authStore.user) {
        let recent = [...(authStore.user.recentProjects || [])];
        recent = recent.filter((p: any) => p.id !== projectId);
        recent.unshift({ id: projectId, title });
        recent = recent.slice(0, 6);
        authStore.user.recentProjects = recent;
        api.user.updateRecentProjects(recent).catch((err: any) => console.error('Failed to sync recent projects:', err));
      }
    },

    async openProject(projectId: string) {
      const meta = this.projectsMetadata[projectId];
      const title = meta?.displayName || projectId;
      this.setLoadedProject({ value: projectId, title });
      this.isOpenProjectDialogOpen = false;
      this.trackRecentProject(projectId, title);
      const uiStore = useUiStore();
      uiStore.setActiveView('dashboard');
    },

    async openRecentProject(p: any) {
      this.setLoadedProject({ value: p.id, title: p.title, role: p.role });
      this.trackRecentProject(p.id, p.title || p.id);
      const uiStore = useUiStore();
      uiStore.setActiveView('dashboard');
    },

    async fetchProjectsMetadata() {
      this.isLoading = true;
      try {
        const response = await api.project.getMetadata();
        this.projectsMetadata = response.projectsMetadata || (response as any).projects || {};
      } catch (err: any) {
        this.error = err.message;
        const authStore = useAuthStore();
        authStore.showNotification(err.message, 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async fetchProjectStats(projectId: string) {
      try {
        const response = await api.project.getStats(projectId);
        this.projectStats = response.project?.stats || (response as any).stats || null;
      } catch (err: any) {
        console.error('Failed to load project statistics:', err);
      }
    },

    async updateProjectMetadata(projectId: string, updates: Partial<ProjectMetadata>) {
      this.isLoading = true;
      try {
        await api.project.updateMetadata(projectId, updates);
        await this.fetchProjectsMetadata();
      } catch (err: any) {
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async createProject(payload: any) {
      this.isLoading = true;
      try {
        await api.project.create(payload);
        await this.fetchProjectsMetadata();
        const uiStore = useUiStore();
        this.setLoadedProject({ value: payload.id, title: payload.displayName });
        this.trackRecentProject(payload.id, payload.displayName);
        uiStore.setActiveView('dashboard');
      } catch (err: any) {
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async deleteProject(projectId: string) {
      this.isLoading = true;
      try {
        await api.admin.deleteProject(projectId);
        await this.fetchProjectsMetadata();
        if (this.loadedProject?.value === projectId) {
          this.setLoadedProject(null);
          const uiStore = useUiStore();
          uiStore.setActiveView('home');
        }
      } catch (err: any) {
        throw err;
      } finally {
        this.isLoading = false;
      }
    }
  }
});
